import { useEffect, useState, type FormEvent } from 'react';
import type { CompanyProfile, UpdateCompanyProfileInput } from '@neuroedge/shared';
import { Button, Card, FileDropzone, PageState, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import {
  openQuotationPreview,
  useCompanyLogoUrl,
  useCompanyProfile,
  useRemoveCompanyLogo,
  useUpdateCompanyProfile,
  useUploadCompanyLogo,
} from '../../api/company-profile';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { resizeImageForUpload } from '../../lib/resize-image';

type Form = Record<keyof UpdateCompanyProfileInput, string>;

const FIELDS: (keyof Form)[] = [
  'companyName',
  'legalName',
  'gstNumber',
  'panNumber',
  'email',
  'phone',
  'alternatePhone',
  'website',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'postalCode',
  'country',
  'quotationTerms',
  'timeZone',
];

/** Every IANA time zone the browser knows (a short list on very old browsers). */
const TIME_ZONES: string[] = (() => {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  return (
    intl.supportedValuesOf?.('timeZone') ?? [
      'Asia/Kolkata',
      'Asia/Dubai',
      'Asia/Singapore',
      'Europe/London',
      'America/New_York',
      'UTC',
    ]
  );
})();

const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const PHONE = /^\+?[0-9][0-9 ()-]{5,19}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEBSITE = /^(https?:\/\/)?[^\s/.]+\.[^\s]+$/i;

function toForm(p: CompanyProfile): Form {
  const out = {} as Form;
  for (const f of FIELDS) out[f] = (p[f] as string | null) ?? '';
  return out;
}

/** Same rules as the API (company-profile.schemas.ts), shown per field before sending. */
function validate(f: Form): Record<string, string> {
  const e: Record<string, string> = {};
  if (f.companyName.trim().length < 2) e.companyName = 'Enter your company name.';
  const gst = f.gstNumber.trim().toUpperCase();
  if (gst && !GSTIN.test(gst))
    e.gstNumber = 'Enter a valid 15-character GSTIN, e.g. 27ABCDE1234F1Z5.';
  const pan = f.panNumber.trim().toUpperCase();
  if (pan && !PAN.test(pan)) e.panNumber = 'Enter a valid 10-character PAN, e.g. ABCDE1234F.';
  if (f.email.trim() && !EMAIL.test(f.email.trim())) e.email = 'Enter a valid email address.';
  for (const k of ['phone', 'alternatePhone'] as const) {
    if (f[k].trim() && !PHONE.test(f[k].trim())) {
      e[k] = 'Enter a valid phone number, e.g. +91 98765 43210.';
    }
  }
  if (f.website.trim() && !WEBSITE.test(f.website.trim())) {
    e.website = 'Enter a valid website, e.g. example.com.';
  }
  return e;
}

function LogoSection({ profile, canManage }: { profile: CompanyProfile; canManage: boolean }) {
  const toast = useToast();
  const upload = useUploadCompanyLogo();
  const remove = useRemoveCompanyLogo();
  const url = useCompanyLogoUrl(profile.logoAssetId);
  const [error, setError] = useState<string | null>(null);

  async function onFile(file: File | null) {
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync(await resizeImageForUpload(file, 800));
      toast.success('Logo updated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload this logo.');
    }
  }

  return (
    <Card title="Logo">
      <p style={{ margin: '0 0 var(--space-3)', color: 'var(--color-text-muted)' }}>
        Printed at the top left of your quotations. A wide PNG with a transparent or white
        background works best. Changes save straight away.
      </p>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        {profile.logoAssetId ? (
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {url ? (
              <img
                src={url}
                alt="Company logo"
                style={{
                  maxWidth: 220,
                  maxHeight: 100,
                  objectFit: 'contain',
                  background: '#fff',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-2)',
                }}
              />
            ) : null}
            {canManage ? (
              <Button
                type="button"
                variant="secondary"
                disabled={remove.isPending}
                onClick={() =>
                  void remove
                    .mutateAsync()
                    .then(() => toast.success('Logo removed.'))
                    .catch((err: unknown) =>
                      setError(err instanceof Error ? err.message : 'Could not remove the logo.'),
                    )
                }
              >
                Remove logo
              </Button>
            ) : null}
          </div>
        ) : (
          <span style={{ color: 'var(--color-text-muted)' }}>No logo yet.</span>
        )}
        {canManage ? (
          <FileDropzone
            accept="image/jpeg,image/png,image/webp"
            file={null}
            onFileSelected={(f) => void onFile(f)}
            placeholder={
              upload.isPending
                ? 'Uploading…'
                : profile.logoAssetId
                  ? 'Drag a new logo here, or click to replace it (JPG or PNG).'
                  : 'Drag your logo here, or click to choose one (JPG or PNG).'
            }
          />
        ) : null}
        {error ? (
          <span className="ne-field__error" role="alert">
            {error}
          </span>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Settings > Company profile: the business details and logo printed on
 * quotations (and wherever the business presents itself to customers).
 * Everyone with settings.read sees it; settings.manage (Owner/Admin) edits.
 */
export function CompanyProfilePage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('settings.manage');
  const toast = useToast();
  const profile = useCompanyProfile();
  const save = useUpdateCompanyProfile();
  const [form, setForm] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (profile.data && !form) setForm(toForm(profile.data));
  }, [profile.data, form]);

  if (profile.isLoading || (!form && !profile.error)) {
    return <PageState kind="loading" title="Loading company profile…" />;
  }
  if (profile.error || !profile.data || !form) {
    return (
      <PageState
        kind="error"
        title="Could not load the company profile"
        message={profile.error?.message}
        onRetry={() => void profile.refetch()}
      />
    );
  }
  const data = profile.data;

  const field = (key: keyof Form, label: string, extra: { placeholder?: string } = {}) => (
    <TextField
      label={label}
      value={form[key]}
      disabled={!canManage}
      placeholder={extra.placeholder}
      onChange={(e) => {
        setForm({ ...form, [key]: e.target.value });
        if (errors[key]) setErrors({ ...errors, [key]: '' });
      }}
      error={errors[key] || undefined}
    />
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    try {
      const saved = await save.mutateAsync({ ...form, timeZone: form.timeZone || undefined });
      setForm(toForm(saved));
      toast.success('Company profile saved.');
    } catch (err) {
      const fieldErrors = fieldErrorsFromApiError(err);
      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length === 0) {
        toast.error(err instanceof Error ? err.message : 'Could not save the company profile.');
      }
    }
  }

  async function preview() {
    setPreviewing(true);
    try {
      await openQuotationPreview();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open the preview.');
    } finally {
      setPreviewing(false);
    }
  }

  const grid = {
    display: 'grid',
    gap: 'var(--space-3)',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  } as const;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 860 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Company profile</h1>
          <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
            Your business details and logo, printed on every quotation you send.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          disabled={previewing}
          onClick={() => void preview()}
        >
          {previewing ? 'Opening…' : 'Preview quotation PDF'}
        </Button>
      </div>

      {!data.saved ? (
        <div
          role="status"
          style={{
            padding: 'var(--space-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-muted)',
          }}
        >
          These details come from your workspace. Check them and save to start using them on
          quotations.
        </div>
      ) : null}

      <LogoSection profile={data} canManage={canManage} />

      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        style={{ display: 'grid', gap: 'var(--space-4)' }}
      >
        <Card title="Business">
          <div style={grid}>
            {field('companyName', 'Company name')}
            {field('legalName', 'Legal name (optional)', {
              placeholder: 'If different, e.g. "… Pvt Ltd"',
            })}
            {field('gstNumber', 'GSTIN (optional)', { placeholder: '27ABCDE1234F1Z5' })}
            {field('panNumber', 'PAN (optional)', { placeholder: 'ABCDE1234F' })}
          </div>
        </Card>

        <Card title="Contact">
          <div style={grid}>
            {field('phone', 'Phone', { placeholder: '+91 98765 43210' })}
            {field('alternatePhone', 'Alternate phone (optional)')}
            {field('email', 'Email', { placeholder: 'sales@example.com' })}
            {field('website', 'Website (optional)', { placeholder: 'example.com' })}
          </div>
        </Card>

        <Card title="Address">
          <div style={grid}>
            {field('addressLine1', 'Address line 1')}
            {field('addressLine2', 'Address line 2 (optional)')}
            {field('city', 'City')}
            {field('state', 'State')}
            {field('postalCode', 'PIN / postal code')}
            {field('country', 'Country')}
          </div>
        </Card>

        <Card title="Time zone">
          <div className="ne-field" style={{ maxWidth: 360 }}>
            <label className="ne-field__label" htmlFor="company-time-zone">
              Workspace time zone
            </label>
            <select
              id="company-time-zone"
              className="ne-input"
              disabled={!canManage}
              value={form.timeZone}
              onChange={(e) => setForm({ ...form, timeZone: e.target.value })}
            >
              {(TIME_ZONES.includes(form.timeZone)
                ? TIME_ZONES
                : [form.timeZone, ...TIME_ZONES]
              ).map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            {errors.timeZone ? <span className="ne-field__error">{errors.timeZone}</span> : null}
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
              Used for &quot;today&quot; on the dashboard, reminder and alert dates, and the date on
              quotations.
            </span>
          </div>
        </Card>

        <Card title="Quotations">
          <div className="ne-field">
            <label className="ne-field__label" htmlFor="quotation-terms">
              Terms &amp; conditions (optional)
            </label>
            <textarea
              id="quotation-terms"
              className="ne-input"
              rows={5}
              maxLength={2000}
              disabled={!canManage}
              placeholder={'e.g. Prices valid for 15 days.\nBank: … IFSC: …'}
              value={form.quotationTerms}
              onChange={(e) => setForm({ ...form, quotationTerms: e.target.value })}
            />
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
              Printed at the end of every quotation - validity, payment terms, bank details.
            </span>
          </div>
        </Card>

        {canManage ? (
          <div>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save company profile'}
            </Button>
          </div>
        ) : (
          <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
            Only an Owner or Admin can change these details.
          </p>
        )}
      </form>
    </div>
  );
}
